<div class="page">
  <header class="page__header">
    <h1 class="page__title">
      <span i18n="@@sr_update.page.header.title">Service Request</span>
      {{ serviceRequest()?.id }}
    </h1>
    <p-breadcrumb [home]="breadcrumbHome" [model]="breadcrumbItems"></p-breadcrumb>
  </header>

  @if (serviceRequest(); as request) {
    <p-card class="page__content">
      <form class="form" [formGroup]="requestUpdateForm">
        <div class="form__field">
          <label for="title" class="input__label" i18n="@@sr_update.field.title.label">Title</label>
          <input
            pInputText
            id="title"
            variant="filled"
            placeholder="Title"
            i18n-placeholder="@@sr_update.field.title.label"
            formControlName="title" />
        </div>
        <div class="form__field form__field--vertical-resizable">
          <label for="description" class="input__label" i18n="@@sr_update.field.description.label">
            Description
          </label>
          <p-editor id="description" [modules]="editorModules" formControlName="description">
            <ng-template pTemplate="header"> </ng-template>
          </p-editor>
          <small class="input__hint" i18n="@@sr_update.field.description.help">
            The better you can describe the issue you are having, the better we can help you
          </small>
        </div>
        <div class="form__field">
          <label for="duedate" class="input__label" i18n="@@sr_update.field.due_date.label">
            Due date
          </label>
          <p-datepicker
            id="duedate"
            tooltipPosition="top"
            dateFormat="dd/mm/yy"
            placeholder="dd/MM/yyyy"
            [minDate]="today"
            formControlName="dueDate" />
          <small class="input__hint" i18n="@@sr_update.field.due_date.help">
            Does the service request need to be fixed by a certain date?
          </small>
        </div>
        <h3 i18n="@@sr_update.page.subtitle.priority">Priority Factors</h3>
        <div class="form__row form__row--quarter">
          <div class="form__field form__field--horizontal">
            <p-toggleswitch id="compliance" formControlName="compliance" />
            <div>
              <label
                for="compliance"
                class="input__label"
                i18n="@@sr_update.field.compliance.label">
                Compliance
              </label>
              <small class="input__hint" i18n="@@sr_update.field.compliance.help">
                Is compliance relevant to the service request?
              </small>
            </div>
          </div>
          <div class="form__field form__field--horizontal">
            <p-toggleswitch id="confidential" formControlName="confidential" />
            <div>
              <label
                for="confidential"
                class="input__label"
                i18n="@@sr_update.field.confidential.label">
                Confidential
              </label>
              <small class="input__hint" i18n="@@sr_update.field.confidential.help">
                Does it involve sensitive data?
              </small>
            </div>
          </div>
          <div class="form__field">
            <label for="severity" class="input__label" i18n="@@sr_update.field.severity.label">
              Severity
            </label>
            <p-select
              id="severity"
              placeholder="Severity"
              i18n-placeholder="@@sr_update.field.severity.placeholder"
              formControlName="severity"
              optionLabel="name"
              [options]="severityOptions" />
            <small class="input__hint" i18n="@@sr_update.field.severity.help">
              What is the impact scope?
            </small>
          </div>
          <div class="form__field">
            <label for="urgency" class="input__label" i18n="@@sr_update.field.urgency.label">
              Urgency
            </label>
            <p-select
              id="urgency"
              placeholder="Urgency"
              i18n-placeholder="@@sr_update.field.urgency.placeholder"
              formControlName="urgency"
              optionLabel="name"
              [options]="urgencyOptions" />
            <small class="input__hint" i18n="@@sr_update.field.urgency.help">
              How fast does it need response?
            </small>
          </div>
        </div>
        @if (request.fileInformation) {
          <div class="form__field">
            <h3 class="input__label" i18n="@@sr_update.field.attached_files.label">
              Attached Files
            </h3>
            @for (file of request.fileInformation; track $index) {
              <a
                tabindex="0"
                target="_blank"
                (click)="downloadFile(file)"
                class="cursor-pointer flex align-items-center">
                <p>{{ file.fileName }}</p>
                <i class="pi pi-arrow-circle-down ml-2 point"></i>
              </a>
            }
          </div>
        }
      </form>
      <div class="page__actions">
        <p-button
          label="Back"
          i18n-label="@@common.button.back"
          class="page__action--start"
          severity="primary"
          (click)="goBack()"
          icon="pi pi-arrow-left" />
        @if (
          checkServiceRequestOwner(request.requester) &&
          request.status !== 'Completed' &&
          request.status !== 'Closed'
        ) {
          <p-button
            label="Close service request"
            i18n-label="@@sr_update.button.close"
            class="page__action--end"
            severity="warn"
            (click)="confirmCloseServiceRequest()" />
        }
        @if (request.status === 'Closed' || request.status === 'Completed') {
          <p-button
            label="Contest closure"
            i18n-label="@@sr_update.button.contest_closure"
            class="page__action--end"
            severity="danger"
            (click)="confirmContestServiceRequest()" />
        }
        <p-button
          label="Join chat"
          i18n-label="@@common.button.join_chat"
          icon="pi pi-send"
          class="page__action--end"
          pTooltip="The ticket has not been assigned to anyone yet, so you can not chat with someone about this ticket"
          [tooltipDisabled]="checkServiceRequestAssignedTo()"
          [disabled]="!checkServiceRequestAssignedTo()"
          (click)="startTeamsChat()" />

        <p-button
          label="Update"
          i18n-label="@@common.button.update"
          class="page__action--end"
          pTooltip="Since you are not the owner of this service request, you cannot update it"
          [tooltipDisabled]="checkServiceRequestOwner(request.requester)"
          [disabled]="!checkServiceRequestOwner(request.requester)"
          (click)="update()" />
      </div>
    </p-card>
  }
</div>

<p-confirmDialog>
  <ng-template pTemplate="message" let-confirmData>
    <div class="flex flex-column align-items-center">
      <p>{{ confirmData.message }}</p>
      <input
        type="text"
        pInputText
        [(ngModel)]="dialogReason"
        placeholder="Enter reason for closing"
        class="w-full" />
    </div>
  </ng-template>
</p-confirmDialog>
